import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import type { Region, View } from "@gooner/shared";
import { VIEW_NAME_MAX } from "@gooner/shared";
import { track } from "../../analytics";
import { api } from "../../lib/api";
import { useSession } from "../../lib/session";
import { type SaveStatus, useViewStore } from "../store/viewStore";
import { useDebouncedCallback } from "../hooks/useDebouncedCallback";
import { useViewConn } from "../hooks/useViewConn";
import { startViewKeepAlive } from "../lib/viewKeepAlive";
import { Button, TextInput, cx } from "../ui/primitives";
import { IconBack, IconPlay } from "../ui/icons";
import { type EditorSubject, SimpleEditor } from "../components/SimpleEditor";
import { AuthModal } from "../components/AuthModal";
import { GuideInfoButton, SurfaceGuide, useSurfaceGuide } from "../../components/SurfaceGuide";
import { GoldDialog } from "../components/GoldDialog";
import { type GoldGate, trackGoldGate } from "../../lib/gold";
import { aspectKey } from "../lib/format";
import { forgetTempView, rememberTempView } from "../lib/recentTempViews";
import { Masked } from "../../lib/replayMask";

/** Same beat as the cave editor's autosave: long enough that a drag is one save. */
const AUTOSAVE_MS = 1200;

/** The header's back button — a link to the library or a history step to a screen, dressed
 *  the same so it reads as one control whose destination depends on where you came from. */
const BACK_CLASS =
  "inline-flex items-center gap-1.5 rounded-md border border-[var(--color-edge)] px-2.5 py-1 text-xs text-zinc-300 transition-colors hover:bg-[var(--color-panel-2)]";

/**
 * Send an edit that the debounce hasn't got around to yet.
 *
 * Read off the store rather than through a hook so it can be called from an effect
 * cleanup, where the component is already on its way out. Only when there is something
 * outstanding: "error" counts, because a failed save leaves the edit unsent too.
 */
function flushPendingSave() {
  const { saveStatus, view } = useViewStore.getState();
  if (!view) return;
  if (saveStatus === "dirty" || saveStatus === "error") void useViewStore.getState().persist();
}

/**
 * Edit one view.
 *
 * Everything here is local-first: a region drag mutates the store immediately and a
 * debounced `persist()` pushes the result, so nothing in the canvas is ever waiting on
 * a request. `useViewConn` relays the same edit over the socket on a much shorter beat, so
 * every screen showing this view tracks the drag while the row catches up once.
 *
 * A temporary view (`t_…`) edits exactly like a saved one; the difference is that it
 * has no row and no owner and will expire, which is why the header says so and offers
 * the one button that turns it into something permanent.
 */
export function ViewEditor() {
  const { id: routeId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  /**
   * Whether a screen sent us here. The pencil on a screen pushes the editor onto that
   * screen's history entry with this flag (DisplayApp's editThisView), so the entry
   * underneath is the screen, already playing the view being edited. That makes "Back to
   * screen" a history step rather than a URL this page would have to know how to build —
   * a device screen and an anonymous one spell theirs differently — and it is exactly
   * what the browser's Back button does, so the two agree.
   */
  const fromScreen =
    (location.state as { fromScreen?: unknown } | null)?.fromScreen === true;

  // Live relay to every screen showing this view. Independent of the autosave: the wire
  // is fast and cheap, a row write is neither.
  useViewConn();

  // This page's guide — the one users named as the confusing surface. Auto-opens on
  // arrival until "don't show this again"; the header's ⓘ brings it back.
  const editorGuide = useSurfaceGuide("editor");

  const view = useViewStore((s) => s.view);
  const loading = useViewStore((s) => s.loading);
  const loadError = useViewStore((s) => s.loadError);
  const saveStatus = useViewStore((s) => s.saveStatus);
  const saveError = useViewStore((s) => s.saveError);
  const saveErrorGold = useViewStore((s) => s.saveErrorGold);
  const isSaved = useViewStore((s) => s.isSaved);
  // Promotion-in-flight specifically, NOT saveStatus: that is shared with the autosave,
  // and keying the button on it makes "Save to library" go dead on every unrelated
  // round trip — longest exactly on the slow links where it is most annoying.
  const promoting = useViewStore((s) => s.promoting);

  // Zustand actions are stable references, so pulling them out one by one keeps every
  // callback below stable too — see the note on `onRegions`.
  const load = useViewStore((s) => s.load);
  const reset = useViewStore((s) => s.reset);
  const applyRegions = useViewStore((s) => s.applyRegions);
  const setAspect = useViewStore((s) => s.setAspect);
  const setAdaptiveLayout = useViewStore((s) => s.setAdaptiveLayout);
  const rename = useViewStore((s) => s.rename);
  const setIcon = useViewStore((s) => s.setIcon);
  const persist = useViewStore((s) => s.persist);
  const saveToLibrary = useViewStore((s) => s.saveToLibrary);
  const user = useSession((s) => s.user);
  const [authOpen, setAuthOpen] = useState(false);
  const [goldGate, setGoldGate] = useState<GoldGate | null>(null);

  // The autosave's Gold backstop, made LOUD. The pre-empt gates read settings cached
  // at page load, so a caps flip mid-session slips past them and lands here as the
  // PUT's 409 — which otherwise dies in the SavePill's tooltip. One modal per refusal:
  // the flag is cleared on open, and the next blocked autosave sets it again.
  useEffect(() => {
    if (!saveErrorGold) return;
    useViewStore.setState({ saveErrorGold: false });
    trackGoldGate("regions");
    setGoldGate("regions");
  }, [saveErrorGold]);

  /**
   * The id SimpleEditor keys its own state off — the id this editing session opened
   * on, not the view's current id.
   *
   * Saving a temporary view mints a permanent id and the URL has to follow it, or a
   * reload would reopen the expiring temporary copy. But to the editor that is the
   * same subject: re-keying on the new id would reset its selection and drop it back
   * to the setup screen the instant after the user saved the layout they were
   * looking at.
   */
  const [subjectKey, setSubjectKey] = useState("");
  /** The last explicit save failure, if it hasn't been superseded by another attempt. */
  const [saveFailure, setSaveFailure] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  /** Why the screen could not be opened (the screen cap, mostly), kept until the next try. */
  const [playError, setPlayError] = useState<string | null>(null);

  /**
   * Watch this view — IN THIS TAB.
   *
   * Works on a temporary view as much as a saved one: the device stores a view id and
   * the server resolves either kind, so an unsaved view can be watched without being
   * kept — which is what the notice below has always claimed. The copy survives the
   * hand-off: the screen's socket joins the view's room and its ping keeps the temporary
   * view alive from the first frame (ws.ts), long before the editor's own beat is missed.
   *
   * It used to claim a new tab, synchronously inside the click, and point it at the
   * minted device — the same gesture contract ViewCard still keeps for the library, where
   * navigating away would take the operator off the page they are working in. Here the
   * operator is DONE working: Play is how people leave the editor ("to get out of
   * editing mode"), and a tab per exit was a tab to close every time until a phone's
   * browser started refusing them. Same reasoning as the screen's pencil, mirrored.
   *
   * TWO WAYS OUT. A screen that sent us here (fromScreen) is already playing this view
   * and has followed every drag live, so there is nothing to mint — Play steps back to it,
   * exactly what the ‹ Screen button and the browser's Back do. From anywhere else a
   * device is minted and this tab becomes it; that screen's pencil is the way back in.
   * A refusal — the screen cap, a 409 — is said in the header rather than swallowed.
   */
  const onPlay = useCallback(() => {
    const current = useViewStore.getState().view;
    if (!current) return;
    // Send any edit the debounce still owes before a screen goes looking for the view.
    flushPendingSave();
    if (fromScreen) {
      navigate(-1);
      return;
    }
    setPlaying(true);
    setPlayError(null);
    void api.devices
      .open({ viewId: current.id, kind: "monitor" })
      .then(({ device }) => navigate(`/view/d/${encodeURIComponent(device.id)}`))
      .catch((e: unknown) =>
        // http() prefixes the server's message with the status ("409: …"); the number
        // is for logs, the sentence after it is for the person.
        setPlayError(
          e instanceof Error ? e.message.replace(/^\d{3}:\s*/, "") : "Couldn’t open a screen.",
        ),
      )
      .finally(() => setPlaying(false));
  }, [fromScreen, navigate]);

  useEffect(() => {
    if (!routeId) return;
    // Already holding it. This is the promotion case: saveToLibrary has put the saved
    // view in the store and swapped the URL underneath us, so there is nothing to
    // fetch — and fetching would flash the loading screen over a live editor.
    if (useViewStore.getState().view?.id === routeId) return;
    // Switching to a different view: the outgoing one's debounced save is about to be
    // replaced, so send it first. Same reason as the unmount flush below.
    flushPendingSave();
    setSubjectKey(routeId);
    // Opening one counts as recent — covers arriving by link or reload, not just by
    // having minted it a moment ago.
    rememberTempView(routeId);
    void load(routeId);
  }, [routeId, load]);

  // Only on the way out, so opening another view never flashes the last one. Not in
  // the effect above: its cleanup runs on every id change, promotion included.
  //
  // The flush is the point. `useDebouncedCallback` cancels its timer on unmount and
  // `reset()` drops the view, so an edit made inside the debounce window — dragging a
  // region and immediately clicking Back — was being discarded rather than deferred,
  // under a pill that said "Saved" the whole time. `persist()` reads the store rather
  // than a closure and captures its body before its first await, so it still sends the
  // right thing even though `reset()` runs on the very next line.
  useEffect(
    () => () => {
      flushPendingSave();
      reset();
    },
    [reset],
  );

  /**
   * Say "still open" while this editor is mounted.
   *
   * An unsaved view expires a fixed grace after its last touch, and editing was the
   * only thing that ever touched it — so opening Build View and then thinking for five
   * minutes destroyed the entry, and the next autosave 404'd onto "Save failed" over
   * work that no longer existed anywhere. The beat carries nothing and writes nothing;
   * it only tells the server the view still has a reader.
   *
   * Keyed on the id alone, not on `view`, whose identity changes on every keystroke —
   * depending on the object would tear down and rebuild the timer constantly. The
   * cleanup stops it on unmount, and a promotion changing the id restarts it against
   * the saved one, where `startViewKeepAlive` declines to beat at all.
   */
  const viewId = view?.id ?? null;
  useEffect(() => {
    if (!viewId) return;
    return startViewKeepAlive(viewId, (id) => void api.views.touch(id).catch(() => undefined));
  }, [viewId]);

  const schedulePersist = useDebouncedCallback(() => void persist(), AUTOSAVE_MS);

  /**
   * Stable by construction — `applyRegions` is a zustand action and
   * `useDebouncedCallback` returns a stable wrapper, so this useCallback never
   * re-fires. An inline arrow here would hand SimpleEditor a fresh callback on every
   * render, which is what silently killed region dragging once already.
   */
  const onRegions = useCallback(
    (regions: Region[]) => {
      applyRegions(regions);
      schedulePersist();
    },
    [applyRegions, schedulePersist],
  );

  const onAspect = useCallback(
    (aspect: { w: number; h: number }) => {
      setAspect(aspect);
      schedulePersist();
    },
    [setAspect, schedulePersist],
  );

  const onAdaptiveLayout = useCallback(
    (on: boolean) => {
      setAdaptiveLayout(on);
      schedulePersist();
    },
    [setAdaptiveLayout, schedulePersist],
  );

  const onRename = useCallback(
    (name: string) => {
      rename(name);
      // Once per actual rename, never per keystroke: every commit site (blur/Enter in
      // ViewHeader, and the editor's view panel) guards on `name !== view.name`. The
      // name itself never travels.
      track("view_renamed");
      schedulePersist();
    },
    [rename, schedulePersist],
  );

  const onIcon = useCallback(
    (icon: string | undefined) => {
      setIcon(icon);
      schedulePersist();
    },
    [setIcon, schedulePersist],
  );

  const onSaveToLibrary = useCallback(async () => {
    // Keeping a view requires an account. Asked for BEFORE the request rather than after
    // a 401, so the answer to "why can't I save this" arrives with the click instead of
    // as an error message about something the operator could not have known.
    if (!user) {
      setAuthOpen(true);
      return;
    }
    setSaveFailure(null);
    // The store refuses a second promotion while one is in flight, so a double-click
    // cannot mint two library rows even if the disabled button below hasn't rendered yet.
    const saved = await saveToLibrary();
    if (!saved) {
      // A promotion refused as a duplicate also returns null, and that is not a failure
      // to report — only an actual error is.
      const { saveStatus: status, saveError: reason, saveErrorGold } = useViewStore.getState();
      // A Gold cap is not a failure to apologize for — it is the upsell's moment. The
      // gate fires here (the 409 IS the gate hit; no pre-empt ran, so nothing double-
      // counts) and the modal carries the pitch; the red banner stays for real errors.
      if (status === "error" && saveErrorGold) {
        trackGoldGate("views");
        setGoldGate("views");
        return;
      }
      // Held here rather than read off the store's saveStatus, which the next successful
      // autosave to the temporary row legitimately overwrites with "Kept for now" about
      // a second later. What the user asked for failed, and that stays on screen until
      // they ask again.
      if (status === "error") setSaveFailure(reason ?? "Couldn’t save this view.");
      return;
    }
    // An edit made while the promotion was in flight is not in the row that was just
    // created — the store keeps it and reports dirty, and nothing else will send it:
    // the URL change below is the promotion case, which the load effect deliberately
    // short-circuits, so its flush never runs. Without this the view sits on amber
    // "Unsaved changes" forever over a correctly-saved row.
    flushPendingSave();
    // It has a row now, so the library lists it. Leaving the temporary id in the
    // unsaved strip too would show one view twice under two ids.
    if (routeId) forgetTempView(routeId);
    // Replace rather than push: the temporary id is now a dead end, and Back should
    // return to wherever this editor was opened from, not to a view that no longer
    // exists. The state rides along so a screen that sent us here is still one step back.
    navigate(`/views/${saved.id}`, { replace: true, state: location.state });
  }, [user, saveToLibrary, navigate, routeId, location.state]);

  const subject = useMemo<EditorSubject | null>(
    () =>
      view
        ? {
            id: subjectKey || view.id,
            name: view.name,
            icon: view.icon,
            aspect: view.aspect,
            adaptiveLayout: view.adaptiveLayout,
            regions: view.regions,
          }
        : null,
    [subjectKey, view],
  );

  if (loading) return <ViewLoading />;
  if (loadError) return <ViewMissing id={routeId} message={loadError} />;
  if (!view || !subject) return <ViewMissing id={routeId} message="View not found." />;

  return (
    <SimpleEditor
      subject={subject}
      onRegions={onRegions}
      onAspect={onAspect}
      onAdaptiveLayout={onAdaptiveLayout}
      onRenameView={onRename}
      onSetIcon={onIcon}
      header={
        <ViewHeader
          view={view}
          isSaved={isSaved}
          saveStatus={saveStatus}
          saveError={saveError}
          onRename={onRename}
          onGuide={editorGuide.openInfo}
          onSaveToLibrary={() => void onSaveToLibrary()}
          onPlay={onPlay}
          playing={playing}
          playError={playError}
          onBackToScreen={fromScreen ? () => navigate(-1) : null}
          saveFailure={saveFailure}
          promoting={promoting}
          signedIn={!!user}
        />
      }
      /* Keeping a view is the one thing that needs an account. Mounted here rather than
         in the header so the prompt outlives any re-render of it. */
      overlay={
        authOpen ? (
          <AuthModal
            initialMode="register"
            reason="Views are saved to your account, so they follow you to every screen you sign in on."
            onClose={() => setAuthOpen(false)}
          />
        ) : goldGate ? (
          <GoldDialog source={goldGate} onClose={() => setGoldGate(null)} />
        ) : editorGuide.source ? (
          <SurfaceGuide surface="editor" source={editorGuide.source} onClose={editorGuide.close} />
        ) : null
      }
    />
  );
}

/* -------------------------------------------------------------------------- */

/**
 * Identity chrome for the view being edited: where you came from, its name, whether
 * it's saved, and — while it isn't — the button that makes it so.
 *
 * The name lives here rather than in SimpleEditor because SimpleEditor edits regions
 * and has no name affordance; identity is the caller's business.
 */
function ViewHeader({
  view,
  isSaved,
  saveStatus,
  saveError,
  onRename,
  onSaveToLibrary,
  onPlay,
  playing,
  playError,
  onBackToScreen,
  saveFailure,
  promoting,
  signedIn,
  onGuide,
}: {
  view: View;
  isSaved: boolean;
  saveStatus: SaveStatus;
  saveError: string | null;
  onRename: (name: string) => void;
  /** Opens this page's guide — the ⓘ beside the region count. */
  onGuide: () => void;
  onSaveToLibrary: () => void;
  /** Watch this view, in this tab: back to the screen that sent us, or onto a fresh one. */
  onPlay: () => void;
  /** True while the device is being minted. */
  playing: boolean;
  /** Why Play could not open a screen (the cap, mostly), kept until the next try. */
  playError: string | null;
  /** Set when a screen sent us here: the back button reads "Screen" and calls this
   *  instead of linking to the library. Null = opened from anywhere else. */
  onBackToScreen: (() => void) | null;
  /** An explicit Save that failed, kept until the user tries again. */
  saveFailure: string | null;
  /** True only while the promotion POST is in flight. */
  promoting: boolean;
  /** Saving needs an account, so the button and the notice say different things. */
  signedIn: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(view.name);

  // A rename in flight shouldn't survive the view changing under it.
  useEffect(() => {
    setEditing(false);
    setDraft(view.name);
  }, [view.id, view.name]);

  const commit = () => {
    const name = draft.trim();
    // A view must have a name — the server rejects a blank one — so an emptied field
    // reverts instead of saving something that would fail on the next persist.
    if (name && name !== view.name) onRename(name);
    else setDraft(view.name);
    setEditing(false);
  };

  const n = view.regions.length;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        {/* ONE way back, and it goes where you came from. A screen's pencil pushes this
            page on top of that screen, so the same button reads "Screen" and steps back
            through history (exactly what the browser's Back does); opened from anywhere
            else it goes to the library — never to `/`, which is the front door. */}
        {onBackToScreen ? (
          <button type="button" onClick={onBackToScreen} className={BACK_CLASS}>
            <IconBack /> Screen
          </button>
        ) : (
          <Link to="/views" className={BACK_CLASS}>
            <IconBack /> Views
          </Link>
        )}

        {editing ? (
          <TextInput
            value={draft}
            autoFocus
            maxLength={VIEW_NAME_MAX}
            aria-label="View name"
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === "Enter") commit();
              if (e.key === "Escape") {
                setDraft(view.name);
                setEditing(false);
              }
            }}
            className="w-56"
          />
        ) : (
          <button
            type="button"
            onClick={() => setEditing(true)}
            title="Rename view"
            className="truncate rounded-md px-1.5 py-1 text-lg font-semibold text-zinc-100 transition-colors hover:bg-[var(--color-panel-2)]"
          >
            {/* The view's name is the person's: masked in replay. The button's
                "Rename view" title stays readable, so the marker is on the text alone. */}
            <Masked>{view.name}</Masked>
          </button>
        )}

        <span className="text-xs text-zinc-500">
          {n} {n === 1 ? "region" : "regions"} · {aspectKey(view.aspect)}
        </span>
        <GuideInfoButton onClick={onGuide} label="How the editor works" />

        <div className="ml-auto flex items-center gap-2">
          <SavePill status={saveStatus} isSaved={isSaved} error={saveError} />
          {/*
            Play is the primary action here, and Save is not, which is a deliberate swap.
            The editor had no way to SEE the thing being edited — while the unsaved notice
            below promised "you can build and preview it either way" — so the affordance
            the page was missing is this one. Saving keeps its own pressure: the amber
            notice, the pill, and the fact that the button appears only while unsaved.
            It plays in THIS tab — see onPlay for where it goes and why no tab is opened.
          */}
          <Button size="sm" variant="primary" disabled={playing} onClick={onPlay}>
            <IconPlay width="14" height="14" className="mr-1.5" />
            {playing ? "Opening…" : "Play"}
          </Button>
          {/* Signed OUT, the button is a phone-hidden nicety: "Sign in to save" is the
              third button in a row that a 390px header already wraps, the amber notice
              below says the same thing, and signing in is one press away in the ☰ on
              every surface. Signed in it is the one real action here, so it stays at
              every width. Same cutoff as the screen bar's phone/desktop split (lg). */}
          {!isSaved ? (
            <span className={signedIn ? "contents" : "hidden lg:contents"}>
              <Button
                size="sm"
                variant="subtle"
                // Two clicks would mint two library rows. The store refuses the second
                // outright; this is the half the user can see.
                disabled={promoting}
                onClick={onSaveToLibrary}
              >
                {promoting ? "Saving…" : signedIn ? "Save to library" : "Sign in to save"}
              </Button>
            </span>
          ) : null}
        </div>
      </div>

      {/* Play could not open a screen — the screen cap, in practice. This used to be a
          silent close of the blank tab; now the server's sentence is shown, and the view
          is still here to try again once a screen has been deleted. */}
      {playError ? (
        <p className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">
          Couldn’t open a screen — {playError}
        </p>
      ) : null}

      {/* An explicit Save that failed. Separate from the pill because the pill tracks
          the autosave, and a successful autosave to the temporary row a second later
          would otherwise wipe the report of the thing the user actually asked for. */}
      {saveFailure ? (
        <p className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">
          Couldn’t save this view to your library — {saveFailure}. It’s still here and
          still unsaved; try again.
        </p>
      ) : null}

      {/* The one piece of state a user can lose, said plainly. An unsaved view is
          only held in memory on the server and expires once nobody is reading it. */}
      {!isSaved ? (
        <p className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
          This view isn’t saved. Edits are kept while you work, but it will disappear once
          you close it —{" "}
          {signedIn ? (
            <>
              <span className="font-semibold">Save to library</span> to keep it.
            </>
          ) : (
            <>
              <span className="font-semibold">sign in</span> to keep it. You can build and
              preview it either way.
            </>
          )}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Where the autosave got to.
 *
 * "idle" is the state before the first edit — it has nothing true to say, so it says
 * nothing rather than claiming "Saved". "dirty" is the 1.2s between an edit and the
 * debounce firing: the pill used to keep showing the PREVIOUS result across that
 * window, which is exactly when the edit is most likely to be lost.
 */
function SavePill({
  status,
  isSaved,
  error,
}: {
  status: SaveStatus;
  isSaved: boolean;
  error: string | null;
}) {
  if (status === "idle") return null;
  const meta =
    status === "dirty"
      ? { label: "Unsaved changes", cls: "border-amber-500/30 text-amber-300", dot: "bg-amber-400" }
      : status === "saving"
      ? { label: "Saving…", cls: "border-[var(--color-edge)] text-zinc-300", dot: "bg-zinc-400 animate-pulse" }
      : status === "error"
        ? { label: "Save failed", cls: "border-red-500/40 text-red-300", dot: "bg-red-400" }
        : isSaved
          ? { label: "Saved", cls: "border-emerald-500/30 text-emerald-300", dot: "bg-emerald-400" }
          : // Persisted, but only to the temporary copy — "Saved" would be a promise
            // this view can't keep.
            { label: "Kept for now", cls: "border-amber-500/30 text-amber-300", dot: "bg-amber-400" };

  return (
    <span
      title={status === "error" ? (error ?? undefined) : undefined}
      className={cx(
        "inline-flex items-center gap-1.5 rounded-full border bg-[var(--color-ink)] px-2.5 py-1 text-[11px] font-medium",
        meta.cls,
      )}
    >
      <span className={cx("h-1.5 w-1.5 rounded-full", meta.dot)} />
      {meta.label}
    </span>
  );
}

function ViewLoading() {
  return (
    <div className="flex h-96 items-center justify-center">
      <div className="flex items-center gap-3 text-zinc-400">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-[var(--color-edge)] border-t-[var(--color-accent)]" />
        Loading view…
      </div>
    </div>
  );
}

function ViewMissing({ id, message }: { id?: string; message: string }) {
  return (
    <div className="mx-auto flex max-w-md flex-col items-center justify-center px-6 py-24 text-center">
      <h2 className="text-lg font-semibold text-zinc-100">Couldn’t open this view</h2>
      <p className="mt-2 text-sm text-zinc-500">
        {id ? `${id}: ` : ""}
        {message}
      </p>
      <p className="mt-2 text-xs text-zinc-600">
        Unsaved views are temporary — one that was never saved to your library doesn’t
        outlive the session.
      </p>
      <Link
        to="/views"
        className="mt-6 inline-flex items-center gap-2 rounded-lg border border-[var(--color-edge)] px-3.5 py-2 text-sm text-zinc-200 hover:bg-[var(--color-panel-2)]"
      >
        <IconBack /> Back to views
      </Link>
    </div>
  );
}
